import PropTypes from "prop-types";
import React, { useState } from "react";
import { capitalize } from "lodash";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Button from "react-bootstrap/Button";
import { AxiosResponse } from "axios";
import {
  getSelectionPresentationKey,
  getSelectionPresentationValue
} from "../utils";
import { LOCATION } from "../../consts";
import Widget from "../../../../forms/components/Widget";
import { Selections } from "../../@types";
import api from "../../../../services/api";
import { prepareQueryAndSelectionsQueryParams } from "../../util";
import { show } from "../../../../notifications/notifications";
import { NotificationsLevel } from "../../../../lib/enums";
import {
  IShareQueryErrorResponse,
  IShareQueryFormErrors,
  IShareQueryProps,
  ShareQueryErrorFields
} from "./@types/ShareQueryModal";

const convertObjectToArrayOfValues = (obj?: {
  [key: number]: Array<string>;
}): Array<string> => Object.values(obj ?? {}).flat();

const createPresentationValue = ({
  query,
  selections = {},
  location
}: {
  query?: string;
  selections?: Selections;
  location?: string;
}) => {
  const selectionsString = Object.entries(selections)
    .map(
      ([key, value]) =>
        `${getSelectionPresentationKey(key)}: ${getSelectionPresentationValue(
          key,
          value
        )}`
    )
    .join("\n");
  return `${query ?? ""}\n${selectionsString}${
    [LOCATION.CLOUD, LOCATION.NETWORK].includes(location ?? "")
      ? `\nLocation: ${capitalize(location)}`
      : ""
  }`.trim();
};

const ShareQuery = ({
  query = "",
  selections = {},
  location,
  name,
  onClose
}: IShareQueryProps) => {
  const initial = {
    query: createPresentationValue({ query, selections, location }),
    addresses: []
  };
  const [errors, setErrors] = useState<IShareQueryFormErrors | undefined>(
    undefined
  );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    const addresses = (e.currentTarget.addresses.value || "")
      .split("\n")
      .filter(Boolean);
    setErrors(undefined);

    return api
      .shareQuery({
        addresses,
        display: initial.query,
        ...prepareQueryAndSelectionsQueryParams(query, selections),
        location
      })
      .then(() => {
        onClose();
        show({
          level: NotificationsLevel.SUCCESS,
          message: "Query sent successfully."
        });
      })
      .catch((error: AxiosResponse<IShareQueryErrorResponse>) => {
        const { detail } = error.data || {};
        if (!detail?.fields) {
          show({
            level: NotificationsLevel.ERROR,
            message:
              "Oops! Something went wrong when sending queries. Please try again."
          });
          return;
        }

        if ((detail?.fields as ShareQueryErrorFields)?.non_field_errors) {
          show({
            level: NotificationsLevel.ERROR,
            message: (
              detail?.fields as ShareQueryErrorFields
            )?.non_field_errors?.join(" ")
          });
          return;
        }

        const addressesErrors: Array<string> =
          detail.fields.addresses instanceof Array
            ? detail.fields.addresses
            : convertObjectToArrayOfValues(detail.fields.addresses);
        const queryErrors =
          detail.fields.query instanceof Array
            ? detail.fields.query
            : convertObjectToArrayOfValues(detail.fields.query);
        setErrors({
          addresses: addressesErrors,
          query: queryErrors
        });
      });
  };

  return (
    <Modal show size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        {name ? (
          <Modal.Title>Share &quot;{name}&quot; query</Modal.Title>
        ) : (
          <Modal.Title>Share Query</Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Widget
            defaultValue={initial.addresses}
            name="addresses"
            label="E-mail addresses"
            help={
              "Enter recipient e-mail addresses. Separate each email address with a new line."
            }
            as={"textarea"}
            rows={6}
            onChange={() => null}
            errors={errors?.addresses}
          />
          <Widget
            defaultValue={initial.query}
            name="query"
            label="Query"
            as="textarea"
            rows={6}
            onChange={() => null}
            errors={errors?.query}
            disabled
          />
          <Form.Group>
            <ButtonToolbar className="justify-content-end">
              <Button
                type="button"
                variant="prompt-secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" variant="prompt-primary">
                Share
              </Button>
            </ButtonToolbar>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

ShareQuery.propTypes = {
  query: PropTypes.string,
  selections: PropTypes.shape({
    submission_time: PropTypes.string,
    submission_user: PropTypes.string,
    processing_status: PropTypes.string,
    classification: PropTypes.string,
    cloud_search_type: PropTypes.string
  }),
  location: PropTypes.string,
  onClose: PropTypes.func,
  name: PropTypes.string
};

export default ShareQuery;
